import classNames from 'clsx';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDescriptorId, isFileWarning } from 'reactApp/modules/uploadList/uploadList.getters';
import { setSelectedErrorAction, updateUploadFilesAction } from 'reactApp/modules/uploadList/uploadList.module';
import { getHighlightSelectedErrorFile, getProblemFiles, getSelectedErrorFile } from 'reactApp/modules/uploadList/uploadList.selectors';
import { Arrows, CloseIcon } from 'reactApp/ui/UploadList/Problems/Controls';
import { Problem } from 'reactApp/ui/UploadList/Problems/Problem';

import styles from './Problems.css';

export const ProblemsContainer = memo(() => {
    const [maxHeight, setMaxHeight] = useState(0);
    const [prevActive, setPrevActive] = useState(false);
    const [nextActive, setNextActive] = useState(false);
    const [fileIndex, setFileIndex] = useState(0);
    const problemFiles = useSelector(getProblemFiles);
    const problemFilesLength = problemFiles.length;
    const selectedFile = useSelector(getSelectedErrorFile);
    const highlightSelectedFile = useSelector(getHighlightSelectedErrorFile);
    const ref = useRef<HTMLDivElement | null>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setSelectedErrorAction({ file: problemFiles[0] }));
    }, []);

    useEffect(() => {
        const findFileIndex = problemFiles.findIndex((file) => getDescriptorId(file) === getDescriptorId(selectedFile));

        if (findFileIndex === -1) {
            return;
        }

        setPrevActive(findFileIndex !== 0);
        setNextActive(findFileIndex !== problemFilesLength - 1);
        setFileIndex(findFileIndex);
    }, [selectedFile, problemFiles.length]);

    const switchWarning = ({ back, highlightFile }: { back: boolean; highlightFile: boolean }) => {
        let nextIndex = fileIndex + 1 >= problemFilesLength ? 0 : fileIndex + 1;

        if (back) {
            nextIndex = fileIndex - 1 < 0 ? problemFilesLength - 1 : fileIndex - 1;
        }

        if (!problemFiles[nextIndex]) {
            return;
        }

        const cloudPath = problemFiles[nextIndex].cloudPath;
        const descriptorId = problemFiles[nextIndex].descriptorId;

        dispatch(setSelectedErrorAction({ file: problemFiles[nextIndex] }));

        if (highlightFile) {
            dispatch(updateUploadFilesAction({ descriptorId, cloudPath, highlight: true }));
        }
    };

    const onClose = () => {
        const file = problemFiles[fileIndex];

        if (!file) {
            return;
        }

        dispatch(updateUploadFilesAction({ descriptorId: getDescriptorId(file), cloudPath: file.cloudPath, hideError: true }));
        switchWarning({ back: true, highlightFile: false });
    };

    useEffect(() => {
        if (!selectedFile) {
            return;
        }

        const fileIndex = problemFiles.findIndex((file) => getDescriptorId(file) === getDescriptorId(selectedFile));
        const problemFile = problemFiles[fileIndex];

        if (!problemFile) {
            switchWarning({ back: true, highlightFile: false });
        }
    }, [problemFiles]);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const height = ref.current.getBoundingClientRect().height;

        if (height !== maxHeight) {
            setMaxHeight(height);
        }
    }, [maxHeight, selectedFile, problemFiles]);

    return (
        <div
            className={classNames({
                [styles.root]: true,
                [styles.highlight]: highlightSelectedFile,
            })}
            style={{ height: `${maxHeight}px` }}
        >
            <div ref={ref} className={styles.items}>
                {problemFiles.map((file, index) => (
                    <Problem key={index} file={file} show={getDescriptorId(file) === getDescriptorId(selectedFile)} />
                ))}
            </div>
            <CloseIcon file={problemFiles[fileIndex]} onClose={onClose} />
            {!isFileWarning(problemFiles[fileIndex]) && (
                <Arrows
                    fileIndex={fileIndex}
                    fileLength={problemFiles.length}
                    prevActive={prevActive}
                    nextActive={nextActive}
                    switchWarning={switchWarning}
                />
            )}
        </div>
    );
});
